import { ACCEL_DEAD_BAND_G } from './accel'

/**
 * Throttle and brake points read from the longitudinal acceleration alone: the Solo 2 logs no
 * throttle or brake channel, so the rider's inputs are inferred from how the speed changes.
 *
 * The lap is split into three phases. Braking (clearly decelerating), neutral (speed held, the
 * small throttle opening that cancels engine braking) and driving (clearly accelerating). A phase
 * change is an event, marked on the racing line where it began:
 *
 * - brake:   into braking from driving or neutral — the braking point.
 * - neutral: braking into neutral — braking ends, the throttle is cracked to hold speed.
 * - drive:   into driving from neutral or straight from braking — the throttle point.
 *
 * Driving tapering to neutral (rolling off at the end of a straight) is a phase change but not a
 * marked event, so a long straight shows only its braking point.
 */
export type Phase = 'brake' | 'neutral' | 'drive'
export type AccelEventKind = Phase

export interface AccelEvent {
  /** Index of the sample where the new phase began */
  index: number
  kind: AccelEventKind
}

/** A phase is entered when the reading passes this (g) ... */
export const PHASE_ENTER_G = 0.1
/** ... and left when it falls back within this of zero (hysteresis against GPS noise). */
export const PHASE_LEAVE_G = ACCEL_DEAD_BAND_G
/** A new phase must last this long (s) before it counts; shorter runs are noise. */
export const PHASE_DWELL_S = 0.4

export const EVENT_COLORS: Record<AccelEventKind, string> = {
  brake: '#ef4444',
  neutral: '#f5b301',
  drive: '#22c55e',
}

export const EVENT_LABELS: Record<AccelEventKind, string> = {
  brake: 'braking point',
  neutral: 'braking ends, throttle held',
  drive: 'throttle point',
}

/** The phase of each sample given the previous one: a Schmitt trigger on the reading. */
function nextPhase(prev: Phase, a: number): Phase {
  if (!Number.isFinite(a)) return prev
  switch (prev) {
    case 'brake':
      if (a <= -PHASE_LEAVE_G) return 'brake'
      return a > PHASE_ENTER_G ? 'drive' : 'neutral'
    case 'drive':
      if (a >= PHASE_LEAVE_G) return 'drive'
      return a < -PHASE_ENTER_G ? 'brake' : 'neutral'
    default:
      if (a > PHASE_ENTER_G) return 'drive'
      if (a < -PHASE_ENTER_G) return 'brake'
      return 'neutral'
  }
}

/**
 * Phase changes along a lap. `accel` is the smoothed longitudinal acceleration in g (see
 * `gpsAccel`) and `time` the matching sample times in seconds.
 */
export function accelEvents(accel: ArrayLike<number>, time: ArrayLike<number>): AccelEvent[] {
  const n = Math.min(accel.length, time.length)
  if (n === 0) return []
  // Raw phase per sample, with hysteresis.
  const raw: Phase[] = new Array(n)
  raw[0] = nextPhase('neutral', accel[0])
  for (let i = 1; i < n; i++) raw[i] = nextPhase(raw[i - 1], accel[i])

  // Runs of a raw phase shorter than the dwell are dropped; the rest are the accepted phases.
  const out: AccelEvent[] = []
  let accepted = raw[0]
  let i = 1
  while (i < n) {
    if (raw[i] === accepted) {
      i++
      continue
    }
    let j = i + 1
    while (j < n && raw[j] === raw[i]) j++
    const lasted = time[j - 1] - time[i]
    if (lasted >= PHASE_DWELL_S) {
      const kind = raw[i]
      // Rolling off from driving is not marked; every other change is.
      if (!(accepted === 'drive' && kind === 'neutral')) out.push({ index: i, kind })
      accepted = kind
    }
    i = j
  }
  return out
}
